'use client';

import React from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import BigNumber from 'bignumber.js';
import { FiCopy, FiCheck, FiChevronLeft, FiChevronRight, FiInbox } from 'react-icons/fi';
import { HiOutlineFire } from 'react-icons/hi';
import styles from './BlockDetailPage.module.css';
import useApiQuery from 'lib/api/useApiQuery';
import useQueryWithPages from 'src/hooks/useQueryWithPages';
import { formatTimestamp } from '../../utils/formatTimestamp';

const BlockDetailPage: React.FC = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const heightOrHash = params?.height_or_hash as string;
  const tab = searchParams?.get('tab') || 'details';
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const blockQuery = useApiQuery('block', {
    pathParams: { height_or_hash: heightOrHash },
    queryOptions: { enabled: Boolean(heightOrHash) },
  });

  const txsQuery = useQueryWithPages({
    resourceName: 'block_txs',
    pathParams: { height_or_hash: heightOrHash },
    options: { enabled: Boolean(heightOrHash) && tab === 'txs' },
  });

  const block = blockQuery.data as any;

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatWei = (value: string | null | undefined) => {
    if (!value || value === '0') return '0 OGPU';
    const bn = BigNumber(value);
    const eth = bn.dividedBy(BigNumber(10).pow(18));
    return `${eth.toFixed(18).replace(/\.?0+$/, '')} OGPU`;
  };

  const formatGwei = (value: string | null | undefined) => {
    if (!value || value === '0') return '0 Gwei';
    const bn = BigNumber(value);
    const gwei = bn.dividedBy(BigNumber(10).pow(9));
    return `${gwei.toFixed(2)} Gwei`;
  };

  const formatWeiShort = (value: string | null | undefined) => {
    if (!value || value === '0') return '0';
    const bn = BigNumber(value);
    const eth = bn.dividedBy(BigNumber(10).pow(18));
    if (eth.isGreaterThanOrEqualTo(1000000)) return `${eth.dividedBy(1000000).toFixed(2)}M`;
    if (eth.isGreaterThanOrEqualTo(1000)) return `${eth.dividedBy(1000).toFixed(2)}K`;
    if (eth.isLessThan(0.000001) && !eth.isZero()) return eth.toExponential(2);
    return eth.toFixed(6);
  };

  const isLoading = blockQuery.isLoading;

  if (!isLoading && !block) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Block not found</div>
      </div>
    );
  }

  const gasUsedPct = block?.gas_used_percentage != null
    ? Number(block.gas_used_percentage).toFixed(2)
    : (block?.gas_used && block?.gas_limit
      ? (Number(block.gas_used) / Number(block.gas_limit) * 100).toFixed(2)
      : '0');

  const txs = txsQuery.data?.items || [];

  const skeleton = (width = '120px') => (
    <span className={styles.skeleton} style={{ width }} />
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Block</h1>
        <span className={styles.blockNumber}>
          {isLoading ? skeleton('80px') : `#${block.height}`}
        </span>
        {!isLoading && (
          <div className={styles.nav}>
            {block.height > 0 && (
              <a href={`/block/${block.height - 1}`} className={styles.navButton}>
                <FiChevronLeft size={16} />
              </a>
            )}
            <a href={`/block/${block.height + 1}`} className={styles.navButton}>
              <FiChevronRight size={16} />
            </a>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <a
          href={`/block/${heightOrHash}?tab=details`}
          className={`${styles.tab} ${tab === 'details' ? styles.tabActive : ''}`}
        >
          Details
        </a>
        <a
          href={`/block/${heightOrHash}?tab=txs`}
          className={`${styles.tab} ${tab === 'txs' ? styles.tabActive : ''}`}
        >
          Transactions {!isLoading ? `(${block.transaction_count || 0})` : ''}
        </a>
      </div>

      {/* Details Tab */}
      {tab === 'details' && (
        <div className={styles.detailCard}>
          <div className={styles.row}>
            <div className={styles.label}>Block Height</div>
            <div className={styles.value}>{isLoading ? skeleton('80px') : block.height}</div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Timestamp</div>
            <div className={styles.value}>{isLoading ? skeleton('220px') : formatTimestamp(block.timestamp)}</div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Transactions</div>
            <div className={styles.value}>
              {isLoading ? skeleton('100px') : (
                <a href={`/block/${heightOrHash}?tab=txs`} className={styles.link}>
                  {block.transaction_count} transaction{block.transaction_count !== 1 ? 's' : ''}
                </a>
              )}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Validated by</div>
            <div className={styles.value}>
              {isLoading ? skeleton('100%') : block.miner ? (
                <>
                  <a href={`/address/${block.miner.hash}`} className={styles.link}>
                    {block.miner.name || block.miner.hash}
                  </a>
                  <button
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(block.miner.hash, 'miner')}
                  >
                    {copiedField === 'miner' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                </>
              ) : '—'}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Size</div>
            <div className={styles.value}>{isLoading ? skeleton('100px') : `${Number(block.size).toLocaleString()} bytes`}</div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Block Hash</div>
            <div className={styles.value}>
              {isLoading ? skeleton('100%') : (
                <>
                  <span className={styles.hashFull}>{block.hash}</span>
                  <button
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(block.hash, 'hash')}
                  >
                    {copiedField === 'hash' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Parent Hash</div>
            <div className={styles.value}>
              {isLoading ? skeleton('100%') : (
                <a href={`/block/${block.height - 1}`} className={styles.link} style={{ fontFamily: 'monospace' }}>
                  {block.parent_hash}
                </a>
              )}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Gas Used</div>
            <div className={styles.value}>
              {isLoading ? skeleton('200px') : (
                <div className={styles.gasBar}>
                  <span>{Number(block.gas_used).toLocaleString()}</span>
                  <span style={{ color: 'rgba(255,255,255,0.3)' }}>({gasUsedPct}%)</span>
                  <div className={styles.gasBarTrack}>
                    <div
                      className={`${styles.gasBarFill} ${
                        Number(gasUsedPct) > 80 ? styles.gasBarOrange :
                        Number(gasUsedPct) > 50 ? styles.gasBarBlue :
                        styles.gasBarGreen
                      }`}
                      style={{ width: `${Math.min(Number(gasUsedPct), 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Gas Limit</div>
            <div className={styles.value}>{isLoading ? skeleton('120px') : Number(block.gas_limit).toLocaleString()}</div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Base Fee per Gas</div>
            <div className={styles.value}>{isLoading ? skeleton('120px') : formatGwei(block.base_fee_per_gas)}</div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>Transaction Fees</div>
            <div className={styles.value}>
              {isLoading ? skeleton('180px') : (
                <div className={styles.feeBreakdown}>
                  <span>{formatWei(block.transaction_fees)}</span>
                </div>
              )}
            </div>
          </div>

          {!isLoading && !!block.burnt_fees && String(block.burnt_fees) !== '0' && (
            <div className={styles.row}>
              <div className={styles.label}>Burnt Fees</div>
              <div className={styles.value}>
                <div className={styles.feeBreakdown}>
                  <span className={styles.burntIcon}><HiOutlineFire size={16} /></span>
                  <span>{formatWei(block.burnt_fees)}</span>
                  {block.burnt_fees_percentage != null && (
                    <span className={styles.feeLabel}>({Number(block.burnt_fees_percentage).toFixed(2)}%)</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isLoading && !!block.priority_fee && String(block.priority_fee) !== '0' && (
            <div className={styles.row}>
              <div className={styles.label}>Priority Fee / Tip</div>
              <div className={styles.value}>{formatWei(block.priority_fee)}</div>
            </div>
          )}

          {!isLoading && block.nonce && (
            <div className={styles.row}>
              <div className={styles.label}>Nonce</div>
              <div className={styles.value} style={{ fontFamily: 'monospace' }}>{block.nonce}</div>
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {tab === 'txs' && (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Txn Hash</th>
                  <th>Method</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Value OGPU</th>
                  <th>Fee OGPU</th>
                </tr>
              </thead>
              <tbody>
                {txs.length === 0 && !txsQuery.isLoading && (
                  <tr>
                    <td colSpan={6}>
                      <div className={styles.emptyState}>
                      <FiInbox className={styles.emptyIcon} size={48} />
                      <p>No transactions in this block</p>
                    </div>
                    </td>
                  </tr>
                )}
                {txs.map((tx: any, i: number) => (
                  <tr key={tx.hash || i}>
                    <td>
                      <a href={`/tx/${tx.hash}`} className={styles.link}>
                        {tx.hash?.slice(0, 10)}...{tx.hash?.slice(-4)}
                      </a>
                      {tx.status === 'ok' && (
                        <span className={styles.statusSuccess} style={{ marginLeft: 6 }}>
                          &#10003;
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={styles.method}>{tx.method || 'Transfer'}</span>
                    </td>
                    <td>
                      <a href={`/address/${tx.from?.hash}`} className={styles.link}>
                        {tx.from?.hash?.slice(0, 8)}...{tx.from?.hash?.slice(-4)}
                      </a>
                    </td>
                    <td>
                      {tx.to ? (
                        <a href={`/address/${tx.to.hash}`} className={styles.link}>
                          {tx.to.hash?.slice(0, 8)}...{tx.to.hash?.slice(-4)}
                        </a>
                      ) : tx.created_contract ? (
                        <a href={`/address/${tx.created_contract.hash}`} className={styles.link}>
                          {tx.created_contract.hash?.slice(0, 8)}...
                          <span className={styles.badge} style={{ marginLeft: 4 }}>Created</span>
                        </a>
                      ) : '—'}
                    </td>
                    <td>{formatWeiShort(tx.value)}</td>
                    <td className={styles.age}>{formatWeiShort(tx.fee?.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {txsQuery.isLoading && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <tbody>
                  {[...Array(5)].map((_, i) => (
                    <tr key={`skel-${i}`}>
                      {[...Array(6)].map((_, j) => (
                        <td key={j}><span className={styles.skeleton} style={{ width: j === 0 ? '140px' : '80px' }} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!txsQuery.isLoading && txs.length > 0 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageButton}
                disabled={!txsQuery.pagination.canGoBackwards}
                onClick={txsQuery.pagination.onPrevPageClick}
              >
                &lt;
              </button>
              <button className={`${styles.pageButton} ${styles.pageButtonActive}`}>
                {txsQuery.pagination.page}
              </button>
              <button
                className={styles.pageButton}
                disabled={!txsQuery.pagination.hasNextPage}
                onClick={txsQuery.pagination.onNextPageClick}
              >
                &gt;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BlockDetailPage;
